import clsx from "clsx";
import React, { ElementType } from "react";
import { twMerge } from "tailwind-merge";
import ControlGroup, {
  ControlGroupContext,
  controlGroupControlStyles,
  useControlGroupContext,
} from "../controlgroup";
import ControlGroupParticipant from "../controlgroup/controlGroupParticipant";
import ControlGroupSegment from "../controlgroup/controlGroupSegment";
import ControlGroupSurface from "../controlgroup/controlGroupSurface";
import Spinner from "../spinner";
import CheckboxControl from "./components/checkbox";
import ColorPickerControl from "./components/color";
import DateTimeControl from "./components/dateTime";
import DisplayControl from "./components/display";
import ControlHelperText from "./components/helperText";
import ControlLabel from "./components/label";
import NumberControl from "./components/number";
import PasswordControl from "./components/password";
import SelectControl from "./components/select";
import SliderControl from "./components/slider";
import TextControl from "./components/text";
import ControlTooltip from "./components/tooltip";
import { useControlContext } from "./context";
import { ControlProps } from "./types";

const ComponentLookup: Record<ControlProps["type"], ElementType> = {
  checkbox: CheckboxControl,
  color: ColorPickerControl,
  datetime: DateTimeControl,
  number: NumberControl,
  select: SelectControl,
  slider: SliderControl,
  text: TextControl,
  password: PasswordControl,
  display: DisplayControl,
};

const groupedControlTypes = new Set<ControlProps["type"]>([
  "datetime",
  "number",
  "password",
  "select",
  "text",
]);

function renderAdornmentSegments(children: React.ReactNode): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (
      React.isValidElement<{ children?: React.ReactNode }>(child) &&
      child.type === React.Fragment
    ) {
      return renderAdornmentSegments(child.props.children);
    }

    if (child == null || typeof child === "boolean") return null;

    return <ControlGroupSegment>{child}</ControlGroupSegment>;
  });
}

export default function Control(props: Readonly<ControlProps>) {
  const {
    startAdornment: startAdo,
    endAdornment: endAdo,
    loading,
    type,
    tooltip,
    tooltipUrl,
    helperText,
    classNames,
    containerDisabled,
    required = false,
    ...rest
  } = props;

  const { name, label, error, size } = props;
  const { readOnly } = useControlContext();
  const group = useControlGroupContext();
  const groupSize = group?.size;
  const usesGroupedShell = groupedControlTypes.has(type);
  const resolvedSize =
    size ?? (usesGroupedShell ? (groupSize ?? "md") : undefined);
  const rendersSurface =
    usesGroupedShell || !!loading || !!startAdo || !!endAdo;
  const surfaceSize = resolvedSize ?? "md";
  const participantSize = size ?? (group ? undefined : surfaceSize);

  const Component = ComponentLookup[type];

  const component = (
    <Component
      {...rest}
      size={resolvedSize}
      segmented={type === "password" ? rendersSurface : undefined}
      className={twMerge(
        usesGroupedShell ? controlGroupControlStyles : undefined,
        classNames?.control,
      )}
      readOnly={rest.readOnly ?? readOnly}
    />
  );

  let control = component;
  if (rendersSurface) {
    control = (
      <ControlGroupContext.Provider value={{ size: surfaceSize }}>
        <ControlGroupSurface
          invalid={!!error}
          className={classNames?.controlGroup}
        >
          {!!loading && (
            <ControlGroupSegment>
              <ControlGroup.Addon>
                <Spinner variant="dark" size={surfaceSize} />
              </ControlGroup.Addon>
            </ControlGroupSegment>
          )}
          {!!startAdo && <>{renderAdornmentSegments(startAdo)}</>}
          {type === "password" ? (
            component
          ) : (
            <ControlGroupSegment grow>{component}</ControlGroupSegment>
          )}
          {!!endAdo && <>{renderAdornmentSegments(endAdo)}</>}
        </ControlGroupSurface>
      </ControlGroupContext.Provider>
    );
  }

  if (!containerDisabled || !!label || !!helperText || !!tooltip) {
    const content = (
      <>
        {(!!label || !!tooltip) && (
          <div className="flex gap-2 items-center mb-1">
            {!!label && type !== "checkbox" && (
              <ControlLabel
                htmlFor={name}
                size={resolvedSize}
                error={error}
                className={classNames?.label}
                required={required}
              >
                {label}
              </ControlLabel>
            )}
            {!!tooltip && (
              <ControlTooltip
                label={label}
                error={error}
                description={tooltip}
                url={tooltipUrl}
              />
            )}
          </div>
        )}
        {control}
        {!!helperText && (
          <ControlHelperText
            size={resolvedSize}
            error={error}
            className={classNames?.helperText}
          >
            {helperText}
          </ControlHelperText>
        )}
      </>
    );

    control = rendersSurface ? (
      <ControlGroupParticipant
        grow={!!group}
        size={participantSize}
        className={twMerge("flex-col", classNames?.container)}
      >
        {content}
      </ControlGroupParticipant>
    ) : (
      <div className={classNames?.container}>{content}</div>
    );
  } else if (rendersSurface) {
    control = (
      <ControlGroupParticipant grow={!!group} size={participantSize}>
        {control}
      </ControlGroupParticipant>
    );
  }

  return control;
}
